import React, { Fragment, useEffect, useState } from "react";

import { getMaximumOrderQuantity } from "@utils/configuration/aem";
import { getPossibleDeliveryOptions } from "@utils/deliveryMethod";
import { DeliveryTypes, ProductVisibilityTypes } from "@utils/enums";
import { ProductConfiguration } from "@utils/productConfiguration/productConfiguration.types";

import { ProductImpressionModel } from "@services/tracking";

import { TPreferredStoreState } from "@redux/modules/api/data/preferredStore/preferredStore.types";
import { TProductDetailState } from "@redux/modules/value/productDetail/productDetail.types";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { WishlistButtonContainer } from "@components/Wishlist/WishlistButton/WishlistButton.container";

import { AdditionalServiceModel } from "lib/@types/service/shared";

import { DeliveryOption, ECommerceProduct, Tooltips } from "../eCommerce.types";
import { Amount } from "./amount/amount.component";
import "./delivery.scss";
import DeliveryBlock from "./deliveryBlock/deliveryBlock.component";
import NoDelivery from "./noDelivery/noDelivery.component";
import OnlyAvailableInStore from "./onlyAvailableInStore/onlyAvailableInStore.component";
import OrderButton from "./orderButton/orderButton.container";

type Props = {
    deliveryOptions: DeliveryOption[];
    visibilityType: ProductVisibilityTypes;
    minimumOrderQuantity: number;
    roundingValue: number;
    preferredStore?: TPreferredStoreState | null;
    product: ECommerceProduct;
    productDetail: TProductDetailState;
    trackingProduct: ProductImpressionModel;
    onConfigurationChange: (config: Record<string, unknown>) => void;
    onQuantityChange: (amount: number) => void;
    onProductDetailChange: (data: Partial<TProductDetailState>) => void;
    configuration: Record<string, unknown>;
    onSubmit: () => void;
    onOpenStorePicker: () => void;
    contextAwareXfPath: string | null;
    infoLink: string;
    tooltips: Tooltips;
    isEcommerceReady: boolean;
    isSoldOut: boolean;
    deliveryTypesDisabled: boolean;
    productConfiguration?: ProductConfiguration;
    webOnly: boolean;
    additionalService: boolean;
    isClient: boolean;
};

const Delivery = ({
    visibilityType,
    deliveryOptions = [],
    minimumOrderQuantity,
    roundingValue,
    preferredStore,
    product,
    productDetail,
    trackingProduct,
    onQuantityChange,
    onProductDetailChange,
    contextAwareXfPath,
    tooltips,
    infoLink,
    onSubmit,
    onOpenStorePicker,
    isEcommerceReady,
    isSoldOut,
    deliveryTypesDisabled,
    onConfigurationChange,
    configuration,
    productConfiguration,
    webOnly,
    additionalService,
    isClient,
}: Props) => {
    const { t } = useGranite();

    const [configurationValid, setConfigurationValid] = useState<boolean>(
        !productConfiguration,
    );

    const changeDelivery = (deliveryType: DeliveryTypes) => {
        if (deliveryType !== DeliveryTypes.ClickAndCollect) {
            if (productDetail.quantity) {
                const options = [];

                for (
                    let i = minimumOrderQuantity;
                    i < getMaximumOrderQuantity();
                    i += roundingValue
                ) {
                    options.push(i);
                }

                const output = options.reduce((prev, curr) =>
                    Math.abs(curr - productDetail.quantity) <
                    Math.abs(prev - productDetail.quantity)
                        ? curr
                        : prev,
                );
                onQuantityChange(output);
            } else {
                onQuantityChange(minimumOrderQuantity);
            }
        } else {
            if (productDetail.quantity) {
                onQuantityChange(productDetail.quantity);
            } else {
                onQuantityChange(1);
            }
        }

        if (productDetail.deliveryType !== deliveryType) {
            onProductDetailChange({
                deliveryType: deliveryType,
            });
        }
    };

    const changeServiceProduct = (
        { sku }: AdditionalServiceModel,
        deliveryType: DeliveryTypes,
    ) => {
        onProductDetailChange({
            deliveryType: deliveryType,
            serviceProductSkus:
                productDetail.serviceProductSkus.includes(sku) &&
                activeDelivery === deliveryType
                    ? productDetail.serviceProductSkus.filter(s => s !== sku)
                    : [...productDetail.serviceProductSkus, sku],
        });
    };

    const onChangeAmount = (value: number) => {
        onQuantityChange(parseInt(value.toString()));
    };

    let activeDelivery = productDetail.deliveryType;

    const possibleDeliveryOptions = getPossibleDeliveryOptions(
        visibilityType,
        deliveryOptions.map(x => x.deliveryType),
    );

    useEffect(() => {
        const oneDeliveryOptionPossible =
            possibleDeliveryOptions.length === 1 &&
            activeDelivery !== possibleDeliveryOptions[0];
        if (oneDeliveryOptionPossible) {
            activeDelivery = possibleDeliveryOptions[0];
            changeDelivery(activeDelivery);
        }
    }, [possibleDeliveryOptions]);

    const noDelivery =
        visibilityType === ProductVisibilityTypes.NotAvailable ||
        visibilityType === ProductVisibilityTypes.TemporaryNotAvailable ||
        visibilityType === ProductVisibilityTypes.Free;

    const onlyAvailableInStore =
        visibilityType === ProductVisibilityTypes.OnlyAvailableInStore;

    const onlyHomeDelivery = visibilityType === ProductVisibilityTypes.Delivery;

    return (
        <Fragment>
            {noDelivery && <NoDelivery visibilityType={visibilityType} />}
            {onlyAvailableInStore && !webOnly && (
                <OnlyAvailableInStore
                    visibilityType={visibilityType}
                    onOpenStorePicker={onOpenStorePicker}
                    sku={productDetail.sku}
                />
            )}
            {!noDelivery && !onlyAvailableInStore && (
                <div>
                    <div className="d-flex flex-column mb-3">
                        <p className="w-100 mb-2">
                            {t("ecommerce.title.delivery")}
                        </p>
                        <DeliveryBlock
                            handleClick={changeDelivery}
                            activeDelivery={productDetail.deliveryType}
                            onServiceProductClick={changeServiceProduct}
                            activeServiceProducts={
                                productDetail.serviceProductSkus
                            }
                            options={deliveryOptions.filter(deliveryOption =>
                                possibleDeliveryOptions.includes(
                                    deliveryOption.deliveryType,
                                ),
                            )}
                            isLimitedStock={product.limitedStock}
                            preferredStore={preferredStore}
                            roundingValue={roundingValue}
                            minimumOrderQuantity={minimumOrderQuantity}
                            xfPath={contextAwareXfPath}
                            tooltips={tooltips}
                            infoLink={infoLink}
                            product={product}
                            deliveryTypesDisabled={deliveryTypesDisabled}
                        />
                    </div>
                    {onlyHomeDelivery && !webOnly && (
                        <div className="mb-3">
                            <OnlyAvailableInStore
                                visibilityType={visibilityType}
                                onOpenStorePicker={onOpenStorePicker}
                                displayEcommerceText={false}
                                sku={productDetail.sku}
                            />
                        </div>
                    )}

                    {productConfiguration && (
                        <productConfiguration.visual
                            configuration={configuration}
                            setConfigurationValid={setConfigurationValid}
                            onConfigurationChange={onConfigurationChange}
                        />
                    )}

                    <div className="d-flex flex-column">
                        <p className="w-100 mb-2">
                            {t("ecommerce.label.amount")}
                        </p>
                        <div className="d-flex mb-2 justify-content-between align-items-center">
                            <div className="ecommerce-amount-wrapper">
                                <Amount
                                    handleChange={onChangeAmount}
                                    value={productDetail.quantity}
                                    roundingValue={
                                        productDetail.deliveryType ===
                                        DeliveryTypes.ClickAndCollect
                                            ? 1
                                            : roundingValue
                                    }
                                    minimumOrderQuantity={
                                        productDetail.deliveryType ===
                                        DeliveryTypes.ClickAndCollect
                                            ? 1
                                            : minimumOrderQuantity
                                    }
                                    maximumOrderQuantity={getMaximumOrderQuantity()}
                                    deliveryType={productDetail.deliveryType}
                                    isUpdatingCart={!isEcommerceReady}
                                />
                            </div>
                            {!additionalService && isClient && (
                                <WishlistButtonContainer
                                    className="ecommerce-wishlist-button"
                                    iconSize={"la-1x"}
                                    sku={product.sku}
                                    trackingProduct={trackingProduct}
                                    labelClassName="d-none d-xl-inline-block"
                                    label={isProductInWishlist =>
                                        isProductInWishlist
                                            ? t(
                                                  "ecommerce.wishlistButton.remove",
                                              )
                                            : t("ecommerce.wishlistButton.add")
                                    }
                                />
                            )}
                        </div>
                        <OrderButton
                            isConfigurationValid={configurationValid}
                            isEcommerceReady={isEcommerceReady}
                            hasMontageParents={
                                product.montageParents.length > 0
                            }
                            isSoldOut={isSoldOut}
                            productDetail={productDetail}
                            onSubmit={onSubmit}
                        />
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default Delivery;
